import { stripSensitiveUrlParams } from "./otherHelpers";

// Kept in sync with functions/utils/sentryHelpers.js -- same fix, same
// reasoning: matched case/separator-insensitively (see normalizeKey below)
// so header-name variants of an already-listed concept don't need their own
// entry, but a genuinely different key (like "x-api-key" vs "apiKey") still
// needs its own listing. See that file's comment for the full incident
// context (a real credential-exposure gap found via Codex review on #1958).
const SENSITIVE_KEYS = [
  "password",
  "newPassword",
  "currentPassword",
  "passwordHash",
  "passwordSalt",
  "token",
  "accessToken",
  "refreshToken",
  "idToken",
  "access_token",
  "refresh_token",
  "secret",
  "clientSecret",
  "client_secret",
  "apiKey",
  "privateKey",
  "authorization",
  "x-api-key",
  "x-auth-token",
  "x-admin-api-key",
  "cookie",
  "set-cookie",
  "x-csrf-token"
];

const normalizeKey = (key) => String(key).toLowerCase().replace(/[^a-z0-9]/g, "");

const SENSITIVE_KEY_SET = new Set(SENSITIVE_KEYS.map(normalizeKey));

const isSensitiveKey = (key) => SENSITIVE_KEY_SET.has(normalizeKey(key));

const REDACTED = "[REDACTED]";

const scrubObject = (value) => {
  if (Array.isArray(value)) {
    return value.map(scrubObject);
  }

  if (!value || typeof value !== "object") {
    return value;
  }

  const entries = Object.entries(value).map(([key, val]) => {
    if (isSensitiveKey(key)) {
      return [key, REDACTED];
    }
    return [key, scrubObject(val)];
  });
  return Object.fromEntries(entries);
};

// scrubObject only redacts by key, so a URL VALUE carrying a sensitive query
// param (e.g. an invite link's inviteToken) under an unlisted key like "url"
// or "to" sails through untouched -- Sentry populates event.request.url and
// breadcrumb.data.url/to/from from the page's own address by default,
// independent of anything the app explicitly logs.
const scrubUrlField = (value) =>
  typeof value === "string" ? stripSensitiveUrlParams(value) : value;

// Deliberately NOT a blanket "scrub every string field" pass: a breadcrumb's
// free-text message (e.g. a console log or an alert's text) isn't guaranteed
// to be a URL, and stripSensitiveUrlParams's relative-path handling would
// silently mangle non-URL text into a bogus path. These three are the only
// fields Sentry/react-router populate with an actual URL/path by convention.
const BREADCRUMB_URL_FIELDS = ["url", "to", "from"];

const scrubBreadcrumb = (breadcrumb) => {
  if (!breadcrumb?.data) {
    return breadcrumb;
  }
  const scrubbedFields = Object.fromEntries(
    BREADCRUMB_URL_FIELDS.filter(
      (field) => typeof breadcrumb.data[field] === "string"
    ).map((field) => [field, scrubUrlField(breadcrumb.data[field])])
  );
  return {
    ...breadcrumb,
    data: { ...breadcrumb.data, ...scrubbedFields }
  };
};

// The Referer request header carries the same full page URL as
// event.request.url (inviteToken included), under a header-name key that
// scrubObject's key-based redaction doesn't list -- redacting it outright
// (like a cookie) would destroy a legitimately useful debugging field, so it
// gets the same param-stripping as every other URL field instead.
const isRefererHeaderKey = (key) =>
  ["referer", "referrer"].includes(normalizeKey(key));

const scrubHeaders = (headers) => {
  if (!headers || typeof headers !== "object") {
    return headers;
  }
  const entries = Object.entries(headers).map(([key, value]) => {
    if (isRefererHeaderKey(key) && typeof value === "string") {
      return [key, scrubUrlField(value)];
    }
    return [key, value];
  });
  return Object.fromEntries(entries);
};

// Firestore closes an idle Listen stream after 60s (PersistentStream's idle
// timer) and reopens it on demand -- an intentional, harmless shutdown. With
// the default fetch-streams transport, @firebase/webchannel-wrapper aborts the
// in-flight fetch via AbortController and leaves the resulting promise without
// a rejection handler, so the browser raises an unhandled
// "AbortError: signal is aborted without reason" (JAVASCRIPT-REACT-13W) that
// carries no app-level failure. Only that exact SDK-originated abort is
// matched here; any other AbortError (including the app's own) still reports.
// TODO: remove once a firebase upgrade (currently 12.12.0) stops leaking this
// rejection from webchannel-wrapper.
const WEBCHANNEL_WRAPPER_MARKER = "webchannel-wrapper";

export const isFirestoreChannelAbort = (name, stack) =>
  name === "AbortError" &&
  typeof stack === "string" &&
  stack.includes(WEBCHANNEL_WRAPPER_MARKER);

const isFirestoreChannelAbortEvent = (event) => {
  const exception = event?.exception?.values?.[0];
  if (!exception || exception.type !== "AbortError") {
    return false;
  }
  if (exception.mechanism?.type !== "onunhandledrejection") {
    return false;
  }
  const frames = exception.stacktrace?.frames || [];
  // Sentry orders frames oldest-first, so the throwing frame is last.
  const topFrame = frames[frames.length - 1];
  const location = `${topFrame?.abs_path || ""} ${topFrame?.filename || ""}`;
  return location.includes(WEBCHANNEL_WRAPPER_MARKER);
};

export const sentryBeforeSend = (event) => {
  if (!event) {
    return event;
  }

  if (isFirestoreChannelAbortEvent(event)) {
    return null;
  }

  const scrubbedRequest = event.request ? scrubObject(event.request) : null;

  return {
    ...event,
    ...(scrubbedRequest
      ? {
          request: {
            ...scrubbedRequest,
            ...(event.request.url ? { url: scrubUrlField(event.request.url) } : {}),
            ...(scrubbedRequest.headers
              ? { headers: scrubHeaders(scrubbedRequest.headers) }
              : {})
          }
        }
      : {}),
    ...(event.extra ? { extra: scrubObject(event.extra) } : {}),
    ...(event.contexts ? { contexts: scrubObject(event.contexts) } : {}),
    ...(event.breadcrumbs
      ? { breadcrumbs: event.breadcrumbs.map(scrubBreadcrumb) }
      : {})
  };
};

// Chrome that never renders customer data, so replays stay diagnosable with
// maskAllText on: we keep "where was the user" while every data value stays
// masked. Body table cells are deliberately absent — that's where vendor
// names, spend and emissions figures live. Alert/toast text is also excluded:
// error strings interpolate customer data ("Failed to load emissions for
// Acme Corp"), and knowing an alert fired is enough without its contents.
export const REPLAY_UNMASKED_SELECTORS = [
  ".MuiDialogTitle-root",
  ".MuiTab-root",
  ".MuiTableCell-head",
  ".MuiButton-root",
  "[data-sentry-unmask]"
];

// Unauthenticated flows where the person filling the form is a third party —
// a customer's employee, an event attendee, an individual consumer — not the
// account holder. Replay is off entirely on these, not merely masked, because
// our Sentry vendor record covers error telemetry rather than screen
// recordings.
//
// "/explorer/create-account" is here for a second, more specific reason:
// invite links put an access-granting inviteToken in that page's query
// string (see otherHelpers.js's SENSITIVE_URL_PARAMS), and completing a
// password signup there can authenticate the user -- starting both Sentry
// Replay and LogRocket (App.js) -- before the app navigates away from that
// URL. Neither recorder's capture path goes through beforeSend/
// beforeSendTransaction, so the token would otherwise reach both third-party
// vendors on every invite acceptance, not just as a rare edge case.
export const REPLAY_EXCLUDED_PATH_PREFIXES = [
  "/employee-survey",
  "/ev",
  "/explorer/create-account"
];

// "/explorer" bare (the Explorer login/landing page) also carries
// inviteToken/teamInvite in its query string -- see the "Login instead"
// redirect in explorer/index.js -- but unlike the prefixes above, every
// authenticated Explorer page also lives under "/explorer/*"
// (/explorer/dashboard, etc.), so a prefix match here would disable
// recording across the entire authenticated app. This needs an exact match
// instead.
const REPLAY_EXCLUDED_EXACT_PATHS = ["/explorer"];

export const shouldRecordReplay = (pathname) =>
  !REPLAY_EXCLUDED_EXACT_PATHS.includes(pathname) &&
  !REPLAY_EXCLUDED_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

export { scrubObject, SENSITIVE_KEYS };
